import { forwardRef, Suspense, useCallback, useImperativeHandle, useState } from 'react';

import { getSanitisedUrl } from '@lyearn/core/utils/url';
import { MediaContainerRef } from '@/components/LegacyMediaContainer';
import { MediaKindEnum } from '@/types/schema';
import dynamic from '@/utils/dynamic';

import { MediaItem } from '../MediaEntity/types';
import { MediaListContainerProps } from './types';

const MediaPlayer = dynamic(
  () =>
    import(
      /* webpackChunkName: "media-player" */
      '@/components/MediaPlayer'
    ),
);

const MediaListContainer = forwardRef<MediaContainerRef, MediaListContainerProps>(
  (
    {
      autoPlay,
      isDownloadable = true,
      medias = [],
      onClose: onCloseProp,
      renderList,
      onMediaPlayerToggle,
      onMediaProgress,
      openMediaPlayerOnLoad,
      allowForwarding,
      allowPlaybackSpeedControl,
      initialProgress,
      allowBackgroundVideoPlayback,
    },
    ref,
  ) => {
    const [pageNumber, setPageNumber] = useState<number | null>(openMediaPlayerOnLoad ? 1 : null);

    const onClick = useCallback(
      (media: MediaItem) => {
        const { kind, url } = media;

        if (kind === MediaKindEnum.WebLink && url) {
          const sanitisedUrl = getSanitisedUrl(url);

          if (sanitisedUrl) {
            window.open(sanitisedUrl);
            return;
          }
        }

        const index = medias.indexOf(media);
        if (index === -1) return;
        setPageNumber(index + 1);
        onMediaPlayerToggle?.(true);
      },
      [medias, onMediaPlayerToggle],
    );
    useImperativeHandle(ref, () => ({ onClick }), [onClick]);

    const onClose = useCallback(() => {
      onMediaPlayerToggle?.(false);
      onCloseProp?.();
      setPageNumber(null);
    }, [onCloseProp, onMediaPlayerToggle]);

    return (
      <>
        {renderList({ medias, onClick })}
        {pageNumber !== null ? (
          <Suspense fallback={null}>
            <MediaPlayer
              allowBackgroundVideoPlayback={allowBackgroundVideoPlayback}
              allowForwarding={allowForwarding}
              allowPlaybackSpeedControl={allowPlaybackSpeedControl}
              autoPlay={autoPlay}
              initialPageNumber={pageNumber}
              initialProgress={initialProgress}
              isDownloadable={isDownloadable}
              medias={medias}
              onClose={onClose}
              onMediaProgress={onMediaProgress}
            />
          </Suspense>
        ) : null}
      </>
    );
  },
);

export default MediaListContainer;
